import { useState } from 'react';
import { formatDateTime, type TemplateVersionDto } from '@pb/shared';
import { copyTemplate, getTemplate, templateVersionFileUrl, uploadTemplateVersion } from '../api/templates';
import { listProjects } from '../api/projects';
import { getWhereUsed } from '../api/whereUsed';
import { Dialog } from '../components/Dialog';
import { FilePicker, ProgressBar } from '../components/FilePicker';
import { HistoryList } from '../components/History';
import { Tabs } from '../components/Nav';
import { EmptyState, ErrorAlert, ErrorState, Loading } from '../components/States';
import { Thumb } from '../components/Thumb';
import { WhereUsedPanel } from '../components/WhereUsed';
import { useProject } from '../lib/project';
import { anyPending } from '../lib/thumbs';
import { usePolling, useResource } from '../lib/useResource';

const TEMPLATE_SLIDES = ['Title slide', 'Agenda slide', 'Chapter divider slide'];

const TOKENS: [string, string][] = [
  ['{{presentation.title}}', 'the presentation’s name'],
  ['{{project.name}}', 'the project’s name'],
  ['{{event.title}}, {{event.audience}}', 'the event’s values; empty without an event'],
  ['{{date}}', 'the event date, or the generation date without an event, as 25 September 2026'],
  [
    '{{chapter.number}}, {{chapter.title}}',
    'the values of the chapter the page belongs to; number empty for unnumbered chapters; both empty on title and agenda pages',
  ],
  [
    '{{agenda}}',
    'on the agenda slide, the only text of its paragraph: replaced by one paragraph per chapter, “<number><tab><title>” for numbered and “<title>” for unnumbered chapters, each with the formatting of the original paragraph',
  ],
];

function TokenReference() {
  return (
    <section className="panel" aria-label="Token reference">
      <div className="panel-title">
        <h3>Token reference</h3>
      </div>
      <table className="table token-table">
        <thead>
          <tr>
            <th>Token</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {TOKENS.map(([token, value]) => (
            <tr key={token}>
              <td>
                {token.split(', ').map((t, i) => (
                  <span key={t}>
                    {i > 0 ? ', ' : null}
                    <code>{t}</code>
                  </span>
                ))}
              </td>
              <td>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="stack small muted" style={{ marginTop: 12 }}>
        <p>
          Tokens are replaced in the text of every generated page (shapes, tables, grouped shapes; not in speaker
          notes), also when PowerPoint has split a token across several text runs. The replacement takes the formatting
          of the run in which the token starts. A <code>{'{{…}}'}</code> that is not in this table is left unchanged and
          reported as a warning of the generation.
        </p>
        <p>
          <strong>Page numbers</strong> appear only where a layout or master carries PowerPoint’s slide-number field.
          The system writes no page numbers itself.
        </p>
        <p>The agenda does not handle overflow: size the agenda text box in the template for the longest agenda.</p>
      </div>
    </section>
  );
}

function UploadTemplateDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { project } = useProject();
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);

  const upload = async () => {
    if (!file) return;
    setProgress(0);
    setError(null);
    try {
      await uploadTemplateVersion(project.id, file, note, setProgress).promise;
      onSaved();
    } catch (err) {
      setError(err);
      setProgress(null);
    }
  };

  const busy = progress !== null;
  return (
    <Dialog
      title="Upload a new template version"
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void upload()} disabled={!file || busy}>
            {busy ? 'Uploading…' : 'Upload template'}
          </button>
        </>
      }
    >
      <p className="small muted">
        A template has exactly three slides in this order: title slide, agenda slide, chapter divider slide. The
        agenda slide contains <code>{'{{agenda}}'}</code> as the only text of one paragraph.
      </p>
      <FilePicker
        file={file}
        onFile={(f) => {
          setFile(f);
          setError(null);
        }}
        disabled={busy}
      />
      {busy ? <ProgressBar value={progress ?? 0} /> : null}
      <div className="field">
        <label htmlFor="template-note">Change note (optional)</label>
        <input
          id="template-note"
          className="input"
          value={note}
          maxLength={2000}
          disabled={busy}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
      <ErrorAlert error={error} />
    </Dialog>
  );
}

function CopyTemplateDialog({ onClose }: { onClose: () => void }) {
  const { project } = useProject();
  const projects = useResource(() => listProjects(true), []);
  const [target, setTarget] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const others = (projects.data ?? []).filter((p) => p.id !== project.id);

  const copy = async () => {
    if (target === '') return;
    setBusy(true);
    setError(null);
    try {
      await copyTemplate(project.id, target);
      setDone(others.find((p) => p.id === target)?.name ?? 'the project');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Copy template to project"
      onClose={onClose}
      closeDisabled={busy}
      footer={
        done ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void copy()} disabled={busy || target === ''}>
              {busy ? 'Copying…' : 'Copy template'}
            </button>
          </>
        )
      }
    >
      {done ? (
        <div className="alert alert-success">The latest template version was copied to “{done}” as its next template version.</div>
      ) : projects.error ? (
        <ErrorState error={projects.error} onRetry={() => void projects.reload()} />
      ) : projects.data === undefined ? (
        <Loading />
      ) : others.length === 0 ? (
        <p className="muted">There is no other project to copy the template to. Create another project first.</p>
      ) : (
        <>
          <p className="small muted">
            The latest version of this template becomes the next template version of the chosen project, with the
            note “Copied from {project.name}, version …”.
          </p>
          <div className="field">
            <label htmlFor="copy-target">Target project</label>
            <select
              id="copy-target"
              className="select"
              value={target}
              onChange={(event) => setTarget(event.target.value ? Number(event.target.value) : '')}
            >
              <option value="">Choose a project…</option>
              {others.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.archived ? ' (archived)' : ''}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      <ErrorAlert error={error} />
    </Dialog>
  );
}

function VersionRow({ version, onRetried }: { version: TemplateVersionDto; onRetried: () => void }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="row-between" style={{ alignItems: 'flex-start' }}>
        <div className="stack" style={{ gap: 4 }}>
          <strong>Version {version.version_no}</strong>
          <span className="small muted">
            {formatDateTime(version.created_at)}
            {version.author ? ` · ${version.author}` : ''}
          </span>
          {version.note ? <span className="small pre">{version.note}</span> : <span className="small faint">No change note.</span>}
        </div>
        <a className="btn btn-sm" href={templateVersionFileUrl(version.id)} download>
          Download .pptx
        </a>
      </div>
      <div className="version-pages" style={{ marginTop: 12 }}>
        {version.pages.map((page, i) => (
          <Thumb
            key={page.page_no}
            thumb={page}
            alt={`Version ${version.version_no}: ${TEMPLATE_SLIDES[i] ?? `page ${page.page_no}`}`}
            size="sm"
            onRetried={onRetried}
          />
        ))}
      </div>
    </div>
  );
}

type TemplateTab = 'template' | 'versions' | 'history';

export function TemplatePage() {
  const { project } = useProject();
  const template = useResource(() => getTemplate(project.id), [project.id]);
  const whereUsed = useResource(() => getWhereUsed('template', project.id), [project.id]);
  const [tab, setTab] = useState<TemplateTab>('template');
  const [dialog, setDialog] = useState<'upload' | 'copy' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const pending = anyPending((template.data?.versions ?? []).flatMap((v) => v.pages).concat(template.data?.latest?.pages ?? []));
  usePolling(pending, () => void template.refresh());

  if (template.error) return <ErrorState error={template.error} onRetry={() => void template.reload()} />;
  if (template.data === undefined) return <Loading label="Loading template…" />;
  const t = template.data;
  const latest = t.latest;
  const versions = [...t.versions].sort((a, b) => b.version_no - a.version_no);

  return (
    <div className="section">
      <div className="page-header">
        <div className="page-title">
          <h2>Template</h2>
          <p className="muted small">
            The master deck every generated presentation of this project is built on
            {latest ? ` — version ${latest.version_no}` : ''}.
          </p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn" onClick={() => setDialog('copy')} disabled={!latest}>
            Copy to project…
          </button>
          {latest ? (
            <a className="btn" href={templateVersionFileUrl(latest.id)} download>
              Download latest
            </a>
          ) : null}
          <button type="button" className="btn btn-primary" onClick={() => setDialog('upload')}>
            {latest ? 'Upload new version' : 'Upload template'}
          </button>
        </div>
      </div>
      {notice ? (
        <div className="alert alert-success row-between" role="status">
          <span>{notice}</span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      <div className="two-col">
        <div className="stack">
          <Tabs
            label="Template views"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: 'template', label: 'Template' },
              { key: 'versions', label: `Versions (${versions.length})` },
              { key: 'history', label: 'History' },
            ]}
          />
          {tab === 'template' ? (
            <>
              {latest ? (
                <div className="grid-pages" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                  {TEMPLATE_SLIDES.map((label, i) => (
                    <figure key={label} className="stack" style={{ margin: 0, gap: 6 }}>
                      <Thumb thumb={latest.pages[i]} alt={label} onRetried={() => void template.refresh()} />
                      <figcaption className="small muted">
                        {i + 1}. {label}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No template yet"
                  action={
                    <button type="button" className="btn btn-primary" onClick={() => setDialog('upload')}>
                      Upload a template
                    </button>
                  }
                >
                  Presentations cannot be generated until the project has a template: a .pptx with a title slide, an
                  agenda slide containing {'{{agenda}}'}, and a chapter divider slide.
                </EmptyState>
              )}
              <TokenReference />
            </>
          ) : null}
          {tab === 'versions' ? (
            versions.length === 0 ? (
              <EmptyState title="No versions yet">Upload a template to create version 1.</EmptyState>
            ) : (
              <div className="stack">
                {versions.map((v) => (
                  <VersionRow key={v.id} version={v} onRetried={() => void template.refresh()} />
                ))}
              </div>
            )
          ) : null}
          {tab === 'history' ? <HistoryList target="template" id={project.id} /> : null}
        </div>
        <aside className="sidebar">
          <WhereUsedPanel target="template" id={project.id} resource={whereUsed} title="Used by" />
        </aside>
      </div>
      {dialog === 'upload' ? (
        <UploadTemplateDialog
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            setNotice('The new template version is saved. New generations use it from now on.');
            void template.refresh();
            void whereUsed.refresh();
          }}
        />
      ) : null}
      {dialog === 'copy' ? <CopyTemplateDialog onClose={() => setDialog(null)} /> : null}
    </div>
  );
}
